import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { ukDayTime, ukDayYear } from "@/lib/time";
import { CaseStamp } from "@/components/portal/crm/CaseStamp";
import { caseFilterKey, caseState, gbp, isOpen } from "@/lib/stages";
import { feeCaption, feeTone, inThisMonth, movement, rowFlag } from "@/lib/caseflow";
import {
  Btn, CellMain, CellSub, Chips, DateCell, DeskSupport, Divider, Empty, Kv, LinkRow, Money, Note, PageBody, PageHead, RailCard, SearchBox, Sec, StatusChip, StatStrip, SubSt, Tb, Td, Th,
} from "@/components/portal/crm/kit";
import { DocRequestsCard } from "@/components/portal/DocRequests";
import { SeatEarnings } from "@/components/portal/SeatEarnings";
import { AdminPreviewBanner } from "./Portal";
import { DeskLetters } from "@/components/portal/crm/DeskLetters";
import { LiveLine } from "@/components/portal/crm/LiveLine";
import { I } from "@/components/portal/sys/icons";
import { AddedFilter } from "@/components/portal/crm/AddedFilter";
import { inRange, matchesCase, newestFirst } from "@/lib/caseList";
import { DocsProgress } from "@/components/portal/crm/DocsProgress";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "new", label: "Submitted", tone: "ink" },
  { key: "in_review", label: "Under assessment", tone: "blue" },
  { key: "awaiting_docs", label: "Awaiting documents", tone: "amber" },
  { key: "preparing_offer", label: "Preparing offer", tone: "teal" },
  { key: "offer_made", label: "Offer issued", tone: "mint" },
  { key: "offer_accepted", label: "Offer accepted", tone: "acc" },
  { key: "completed", label: "Funded", tone: "green" },
  { key: "declined", label: "Declined", tone: "red" },
  { key: "archived", label: "Archived" },
];

const stageKey = caseFilterKey;

export default function Workspace() {
  const { leads, summary, projection, me, isAdmin, agent } = useOutletContext();
  const nav = useNavigate();
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [added, setAdded] = useState({ key: "any" });
  // Archived cases leave the live list — fetched only when the filter asks for them.
  const [archived, setArchived] = useState(null);

  useEffect(() => {
    if (filter !== "archived" || archived !== null) return;
    api.get("/refer/leads", { params: { archived: 1 } })
      .then((r) => setArchived(r.data.items || [])).catch(() => setArchived([]));
  }, [filter, archived]);

  const byId = useMemo(() => {
    const m = {};
    (projection?.cases || []).forEach((c) => { m[c.lead_id] = c; });
    return m;
  }, [projection]);

  const counts = useMemo(() => {
    const live = leads.filter((l) => inRange(l.created_at, added));
    const c = { all: live.length, archived: (archived || []).filter((l) => inRange(l.created_at, added)).length };
    FILTERS.forEach((f) => {
      if (!["all", "archived"].includes(f.key)) c[f.key] = live.filter((l) => stageKey(l) === f.key).length;
    });
    return c;
  }, [leads, archived, added]);

  const rows = useMemo(() => {
    const source = filter === "archived" ? (archived || []) : leads;
    return newestFirst(source).filter((l) => {
      if (!inRange(l.created_at, added)) return false;
      if (!["all", "archived"].includes(filter) && stageKey(l) !== filter) return false;
      return matchesCase(l, q);
    });
  }, [leads, archived, filter, q, added]);

  const live = leads.filter((l) => isOpen(l.stage));
  const awaiting = leads.filter((l) => l.stage === "sent").length;
  const fundedThisMonth = leads.filter((l) => l.stage === "completed" && inThisMonth(l.updated_at)).length;
  const t = projection?.totals;
  const onTerms = (projection?.cases || []).filter((c) => c.counted && c.state !== "funded");
  const fundedAwaiting = (projection?.cases || []).filter((c) => c.state === "funded");
  const onTermsTotal = onTerms.reduce((s, c) => s + (c.likely_gbp || 0), 0);
  const fundedTotal = fundedAwaiting.reduce((s, c) => s + (c.likely_gbp || 0), 0);

  const needsYou = useMemo(() => {
    const out = [];
    leads.forEach((l) => {
      const who = l.business_name || l.applicant_name;
      const st = caseState(l);
      const k = st.key;
      if (st.now?.key === "no_contact") out.push({ id: l.id, line: `${who} — trying to reach your client · help the desk get hold of them` });
      else if (st.now?.key === "callback") out.push({ id: l.id, line: `${who} — call back booked · make sure your client is free` });
      else if (l.docs_open > 0) out.push({ id: l.id, line: `${who} — ${l.docs_open} document${l.docs_open === 1 ? "" : "s"} outstanding` });
      else if (k === "quiet") out.push({ id: l.id, line: `${who} — chase` });
      else if (l.stage === "declined") out.push({ id: l.id, line: `${who} — needs a new home` });
      else if (k === "offer_declined") out.push({ id: l.id, line: `${who} — client turned the offer down · any steer for the desk?` });
      else if (l.stage === "offer_made" && k !== "offer_accepted") out.push({ id: l.id, line: `${who} — offer to accept` });
    });
    return out.slice(0, 5);
  }, [leads]);

  const unsigned = me?.agreement?.status !== "signed";

  const rail = agent ? (
    <>
      <DeskSupport body="Stuck on a case? Open it and ask in the thread — the desk answers there, seven days a week." chat={false} />

      <RailCard title="Needs you today" testId="referrer-needs-you">
        {needsYou.length === 0
          ? <div style={{ fontSize: 13, color: "var(--mut)" }}>Nothing is waiting on you. Every live case is with a desk.</div>
          : needsYou.map((n) => (
            <LinkRow key={n.id + n.line} label={n.line} onClick={() => nav(`/introducer/cases/${n.id}`)}
                     testId={`referrer-needs-${n.id}`} />
          ))}
      </RailCard>

      <RailCard title="Your seat" testId="referrer-seat-card">
        <Kv rows={[
          ["Company", me?.company || me?.name || "—"],
          ["You", me?.seat?.name || "—"],
          ["You see", "The cases you put in"],
          ...(me?.seat?.first_funded_at ? [["First case funded", ukDayYear(me.seat.first_funded_at), { "data-testid": "referrer-seat-first-funded", style: { color: "var(--green)", fontWeight: 700 } }]] : []),
        ]} />
      </RailCard>

      <SeatEarnings variant="rail" />

      <Note testId="referrer-projection-note">Every case you submit is attributed to {me?.company || "your company"}. Commission and statements sit with the account holder.</Note>
    </>
  ) : (
    <>
      <DeskSupport body="Case management, lender chasing and payouts. Seven days a week." />

      <RailCard title="Needs you today" testId="referrer-needs-you">
        {needsYou.length === 0
          ? <div style={{ fontSize: 13, color: "var(--mut)" }}>Nothing is waiting on you. Every live case is with a desk.</div>
          : needsYou.map((n) => (
            <LinkRow key={n.id + n.line} label={n.line} onClick={() => nav(`/introducer/cases/${n.id}`)}
                     testId={`referrer-needs-${n.id}`} />
          ))}
      </RailCard>

      <RailCard title="Your partnership" testId="referrer-partnership">
        <Kv rows={[
          ["Standard commission", projection?.model?.rate || "—"],
          ["Reference", me?.tracking_code || "—"],
          ["Payout day", (summary?.payout_frequency || "monthly") === "weekly" ? "Fridays" : "1st"],
        ]} />
        <Divider style={{ margin: "18px 0" }} />
        <LinkRow label="Payout bank details" onClick={() => nav("/introducer/account")} />
        <LinkRow label="Introducer agreement" onClick={() => nav("/introducer/paperwork")} />
        <LinkRow label="Media kit" onClick={() => nav("/introducer/media-kit")} />
      </RailCard>

      <Note testId="referrer-projection-note">{projection?.note}</Note>
    </>
  );

  return (
    <>
      {isAdmin && <AdminPreviewBanner />}

      {unsigned && !agent && (
        <div className="warn" data-testid="agreement-callout" style={{ marginBottom: 22 }}>
          <span>
            <b>Your introducer agreement is waiting on your signature.</b> It takes about twenty seconds in the
            portal, and commission is only payable on signed terms.{" "}
            <Link to="/introducer/paperwork" style={{ fontWeight: 800, textDecoration: "underline" }}>Read it and sign</Link>
          </span>
        </div>
      )}

      <PageHead
        eyebrow={agent ? `Your seat at ${me?.company || me?.name || "the company"}` : "Whole organisation"}
        title="My cases"
        lede={agent
          ? "The cases you have put in, their stage and what the desk is waiting on."
          : "Every case your team has introduced, its stage and the fee it is carrying."}
        testId="referrer-workspace-head"
        actions={
          <>
            {!agent && <Btn as={Link} to="/introducer/statements" testId="referrer-statement-cta">Payout statement</Btn>}
            <Btn kind="primary" as={Link} to="/introducer/submit" testId="referrer-submit-cta">
              <I.plus /> Submit a case
            </Btn>
          </>
        }
      />

      <PageBody rail={rail}>
        <StatStrip
          testId="referrer-stats"
          items={agent ? [
            { k: "Live cases", v: String(live.length), h: `${awaiting} waiting on a desk reply`, testId: "referrer-stat-live" },
            { k: "Funded this month", v: String(fundedThisMonth), h: `${leads.filter((l) => l.stage === "completed").length} funded all time`, testId: "referrer-stat-funded" },
            { k: "Submitted", v: String(leads.length), h: "Cases you have put in", testId: "referrer-stat-submitted" },
            { k: "Needs you", v: String(needsYou.length), h: needsYou.length ? "Open the case to act" : "Nothing waiting on you", testId: "referrer-stat-needs" },
          ] : [
            { k: "Live cases", v: String(live.length), h: `${awaiting} waiting on a desk reply`, testId: "referrer-stat-live" },
            { k: "Funded this month", v: String(fundedThisMonth), h: `${summary?.completed ?? 0} funded all time`, testId: "referrer-stat-funded" },
            { k: "Paid to you", v: gbp(summary?.paid_gbp), h: summary?.last_payout_at ? <>Last payout <b>{ukDayYear(summary.last_payout_at)}</b></> : "Nothing settled yet", testId: "referrer-stat-paid" },
            { k: "Projected fees", v: gbp(t?.likely_gbp), h: `From ${t?.live_cases ?? 0} live case${t?.live_cases === 1 ? "" : "s"} with a desk`, testId: "referrer-stat-projected" },
          ]}
        />

        <div className="sec" style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <SearchBox value={q} onChange={setQ} placeholder="Search name, company, email, phone or reference" testId="referrer-search" />
          <AddedFilter value={added} onChange={setAdded} testId="referrer-added" />
          <div style={{ marginLeft: "auto", maxWidth: "100%", minWidth: 0, overflowX: "auto" }} data-testid="referrer-stage-tabs">
            <Chips items={FILTERS.map((f) => ({ ...f, count: counts[f.key] || 0 }))} value={filter}
                   onChange={setFilter} testPrefix="referrer-filter" />
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }} data-testid="referrer-applications">
          {rows.length === 0 ? (
            <Empty testId="referrer-cases-empty">
              {leads.length === 0 ? "Nothing introduced yet." : "No case matches that view."}
              {leads.length === 0 && (
                <div style={{ marginTop: 18 }}>
                  <Btn kind="primary" as={Link} to="/introducer/submit">Submit your first case</Btn>
                </div>
              )}
            </Empty>
          ) : (
            <Tb
              testId="referrer-leads-table"
              head={<>
                <Th style={{ width: "27%" }}>Client</Th>
                <Th style={{ width: "21%" }}>Facility</Th>
                <Th style={{ width: "17%" }}>Stage</Th>
                <Th style={{ width: "18%" }}>Last movement</Th>
                {!agent && <Th className="r" style={{ width: "17%" }}>Your fee</Th>}
              </>}
            >
              {rows.map((l) => {
                const st = caseState(l);
                const p = byId[l.id];
                const mv = movement(l);
                return (
                  <tr key={l.id} className={rowFlag(l)} style={{ cursor: "pointer" }}
                      onClick={() => nav(`/introducer/cases/${l.id}`)} data-testid={`referrer-lead-row-${l.id}`}>
                    <td className="lead">
                      <CellMain>{l.business_name || l.applicant_name}</CellMain>
                      <CellSub>
                        {l.applicant_name}{l.applicant_position ? `, ${l.applicant_position}` : ""}
                        {l.customer_submitted && (
                          <span style={{ fontWeight: 700, color: "var(--accent-text)" }} data-testid={`referrer-lead-via-${l.id}`}> · via your link</span>
                        )}
                        {l.submitted_via === "api" && (
                          <span style={{ fontWeight: 700, color: "var(--accent-text)" }} data-testid={`referrer-lead-api-${l.id}`}>
                            {" "}· via API{l.duplicate_of ? " · possible duplicate" : ""}
                          </span>
                        )}
                        {!agent && l.submitted_by_name && (
                          <span style={{ fontWeight: 700, color: "var(--ink-3)" }} data-testid={`referrer-lead-by-${l.id}`}> · put in by {l.submitted_by_name}</span>
                        )}
                      </CellSub>
                      <div><CaseStamp at={l.created_at} testId={`referrer-lead-stamp-${l.id}`} /></div>
                    </td>
                    <Td><Money v={l.amount_gbp} sub={`${l.finance_type}${l.term_months ? ` · ${l.term_months} mo` : ""}`} /></Td>
                    <Td>
                      <StatusChip state={st} testId={`referrer-lead-stage-${l.id}`} />
                      {st.key === "awaiting_docs" && <div><DocsProgress progress={l.docs_progress} variant="mini" testId={`referrer-lead-docs-progress-${l.id}`} /></div>}
                      {(l.routes || []).length
                        ? <div><DeskLetters desks={l.routes} testId={`referrer-lead-desks-${l.id}`} /></div>
                        : <SubSt testId={`referrer-lead-passed-${l.id}`}>{st.sub}</SubSt>}
                      <LiveLine move={l.move} testId={`referrer-lead-live-${l.id}`} />
                    </Td>
                    <Td>
                      <DateCell>{ukDayTime(l.move?.at || mv.at, "—")}</DateCell>
                      {/* The stage chip already carries its own caption, and the live line says what moved. */}
                      {!l.move && mv.what !== st.sub && <CellSub>{mv.what}</CellSub>}
                    </Td>
                    {!agent && (
                      <Td className="r">
                        {p
                          ? <Money v={p.likely_gbp} tone={feeTone(p)} sub={feeCaption(p)} testId={`referrer-lead-fee-${l.id}`} />
                          : <Money v={0} tone="muted" sub="Not counted yet" />}
                      </Td>
                    )}
                  </tr>
                );
              })}
            </Tb>
          )}
          <div className="card-f">
            <span>
              Showing {rows.length === leads.length ? `all ${leads.length}` : `${rows.length} of ${leads.length}`} case{leads.length === 1 ? "" : "s"}.
              A case shows the desks it sits with as a count, never by name.
            </span>
            <span style={{ fontWeight: 700, color: "var(--ink-3)" }}>{ukDayTime(new Date().toISOString())} UK</span>
          </div>
        </div>

        {!agent && <Sec
          title="Projected fees, and how they are worked out"
          sub="Only cases a desk has accepted count. Nothing here is a forecast of what might happen."
          right={<Btn sm as={Link} to="/introducer/performance" testId="referrer-see-projection">Full method</Btn>}
        >
          <div className="card">
            <div className="card-b proj-split">
              <div style={{ minWidth: 0 }}>
                {onTerms.length + fundedAwaiting.length === 0 ? (
                  <Empty>Nothing on terms yet. A case starts carrying a figure the moment a desk accepts it for review.</Empty>
                ) : (
                  <Tb head={<><Th>Case</Th><Th>Facility on terms</Th><Th>Basis</Th><Th className="r">Projected fee</Th></>}>
                    {[...fundedAwaiting, ...onTerms].map((c) => (
                      <tr key={c.lead_id} style={{ cursor: "pointer" }} onClick={() => nav(`/introducer/cases/${c.lead_id}`)}
                          data-testid={`referrer-projected-row-${c.lead_id}`}>
                        <td className="lead">
                          <CellMain>{c.business_name || c.applicant_name}</CellMain>
                          <CellSub>{c.state === "funded" ? "Funded, awaiting payout"
                            : (() => { const l = leads.find((x) => x.id === c.lead_id); return l ? caseState(l).label : c.state === "offer" ? "Offer out" : "In review"; })()}</CellSub>
                        </td>
                        <Td><Money v={c.amount_gbp} /></Td>
                        <Td><CellSub style={{ margin: 0 }}>{projection?.model?.rate} · {c.desks_live} desk{c.desks_live === 1 ? "" : "s"}</CellSub></Td>
                        <Td className="r"><Money v={c.likely_gbp} tone={c.state === "funded" ? "green" : "ink"} /></Td>
                      </tr>
                    ))}
                  </Tb>
                )}
              </div>
              <div>
                <div className="rail-t">The arithmetic</div>
                <Kv rows={[
                  ["On accepted terms", gbp(onTermsTotal)],
                  ["Funded, not yet paid", <span className="money green" style={{ fontSize: 14 }} key="f">{gbp(fundedTotal)}</span>],
                  ["Already paid to you", gbp(summary?.paid_gbp)],
                ]} />
                <Divider />
                <div className="rail-t" style={{ marginBottom: 8 }}>Projected total</div>
                <div className="money" style={{ fontSize: 28 }} data-testid="referrer-projected-total">
                  {gbp((t?.likely_gbp || 0) + (summary?.paid_gbp || 0))}
                </div>
                <p style={{ color: "var(--mut)", fontSize: 12.5, margin: "14px 0 0" }}>
                  Cases no desk has accepted are excluded — they read “not counted yet” in your list.
                  A case that completes at a different figure recalculates the same day.
                </p>
              </div>
            </div>
          </div>
        </Sec>}

        {!isAdmin && (
          <Sec title="Documents your clients still owe us"
               sub="One tap sends them a private upload link by text and email. It lands straight on the case.">
            <DocRequestsCard role="referrer" title={null} subtitle={null} />
          </Sec>
        )}
      </PageBody>
    </>
  );
}
